import { cardAnatomy, accordionAnatomy, menuAnatomy } from "@chakra-ui/anatomy";
import { createMultiStyleConfigHelpers, defineStyle, defineStyleConfig, extendTheme, type ThemeConfig } from "@chakra-ui/react";

const cardStyler = createMultiStyleConfigHelpers(cardAnatomy.keys)
const acordionStyler = createMultiStyleConfigHelpers(accordionAnatomy.keys)
const menuStyler = createMultiStyleConfigHelpers(menuAnatomy.keys)

const config: ThemeConfig = {
    initialColorMode: 'system',
    useSystemColorMode: true,
}

const theme = extendTheme({
    config,
    colors: {
        brand: {
            50: '#f0fdfa',
            100: '#e0f2fe',
            200: '#bae6fd',
            300: '#7dd3fc',
            400: '#38bdf8',
            500: '#0ea5e9',
            600: '#0284c7',
            700: '#0369a1',
            800: '#075985',
            900: '#0c4a6e',
        },
        gray: {
            750: '#1e2638',
            850: '#121824',
        },
    },
    styles: {
        global: (props: any) => ({
            body: {
                bg: props.colorMode === 'light' ? 'gray.50' : 'gray.900',
            }
        })
    },
    components: {
        Card: cardStyler.defineMultiStyleConfig({
            baseStyle: {
                container: {
                    borderRadius: "xl",
                }
            },
            variants: {
                outline: (props) => cardStyler.definePartsStyle({
                    container: {
                        border: `1px solid ${props.colorScheme === 'light' ? 'gray.200' : 'gray.700'}`,
                        bg: props.colorMode === 'light' ? 'white' : 'gray.800',
                        boxShadow: "sm",
                        transition: "all 0.2s ease-in-out",
                        _hover: {
                            boxShadow: "md",
                            borderColor: "brand.400",
                            transform: "translateY(-2px)"
                        }
                    }
                }),
                elevated: (props) => cardStyler.definePartsStyle({
                    container: {
                        bg: props.colorMode === 'light' ? 'white' : 'gray.800',
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    }
                })
            }
        }),
        Accordion: acordionStyler.defineMultiStyleConfig({
            variants: {
                fileNav: acordionStyler.definePartsStyle({
                    root: {
                        p: 0,
                        border: 'none'
                    },
                    container: {
                        p: 0,
                        border: 'none'
                    }, 
                    button: {
                        flexDir: 'row',
                        p: 2, 
                        gap: 2,
                        fontSize: 'sm',
                        border: 'none',
                        _hover: {
                            bg: 'gray.100',
                            borderRadius: 'md'
                        }
                    },
                    panel: {
                        p: 0, 
                        paddingLeft: 7,
                    }
                })
            }
        }),
        Button: defineStyleConfig({
            baseStyle: {
                borderRadius: "lg",
                fontWeight: "bold",
            },
            variants: {
                solid: (props) => {
                    const isBrandGradient = ['green', 'teal', 'emerald', 'brand'].includes(props.colorScheme);
                    return {
                        bg: isBrandGradient 
                            ? 'linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)' 
                            : `${props.colorScheme}.500`,
                        color: 'white',
                        _hover: {
                            bg: isBrandGradient 
                                ? 'linear-gradient(135deg, #0284c7 0%, #059669 100%)' 
                                : `${props.colorScheme}.600`,
                            boxShadow: isBrandGradient ? "0 4px 14px rgba(14, 165, 233, 0.35)" : "lg",
                            transform: isBrandGradient ? "translateY(-1px)" : undefined,
                        }
                    };
                },
                outline: (props) => {
                    const isBrand = ['green', 'teal', 'emerald', 'brand'].includes(props.colorScheme);
                    return defineStyle({
                        borderColor: isBrand ? '#0ea5e9' : `${props.colorScheme}.500`,
                        color: isBrand ? (props.colorMode === 'light' ? '#0284c7' : '#38bdf8') : `${props.colorScheme}.500`,
                        _hover: {
                            bg: isBrand ? 'rgba(14, 165, 233, 0.08)' : `${props.colorScheme}.50`,
                            borderColor: isBrand ? '#10b981' : undefined,
                            color: isBrand ? (props.colorMode === 'light' ? '#0f766e' : '#34d399') : undefined,
                        }
                    });
                },
                ghost: (props) => {
                    const isBrand = ['green', 'teal', 'emerald', 'brand'].includes(props.colorScheme);
                    return defineStyle({
                        color: isBrand ? (props.colorMode === 'light' ? '#0284c7' : '#38bdf8') : `${props.colorScheme}.600`,
                        _hover: {
                            bg: isBrand ? 'rgba(14, 165, 233, 0.08)' : `${props.colorScheme}.50`,
                        }
                    });
                },
                inline: (props) => defineStyle({
                    borderRadius: 0,
                    border: 'none',
                    bg: `${props.colorScheme}.${props.colorMode === 'light' ? 100 : 700}`,
                    color: `${props.colorScheme}.${props.colorMode === 'light' ? 700 : 100}`
                })
            }
        }),
        Menu: menuStyler.defineMultiStyleConfig({
            baseStyle: (props) => ({
                list: {
                    bg: props.colorMode === 'light' ? 'white' : '#141c28',
                    borderColor: props.colorMode === 'light' ? 'gray.200' : 'rgba(255, 255, 255, 0.12)',
                    boxShadow: '2xl',
                    borderRadius: 'xl',
                    py: 1.5,
                    px: 1.5,
                },
                item: {
                    bg: 'transparent',
                    color: props.colorMode === 'light' ? 'gray.800' : 'gray.100',
                    borderRadius: 'lg',
                    _hover: {
                        bg: props.colorMode === 'light' ? 'rgba(14, 165, 233, 0.08)' : 'rgba(14, 165, 233, 0.15)',
                        color: props.colorMode === 'light' ? '#0284c7' : '#38bdf8',
                    },
                    _focus: {
                        bg: props.colorMode === 'light' ? 'rgba(14, 165, 233, 0.08)' : 'rgba(14, 165, 233, 0.15)',
                        color: props.colorMode === 'light' ? '#0284c7' : '#38bdf8',
                    }
                }
            })
        })
    }
})

export default theme;